import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { Button, Input } from "@/design-system/components/ui";
import { brokersApi } from "@/api/brokers";
import { brokerPickerLabel } from "@/lib/brokerDisplay";
import type { QuestionType } from "@/api/reportTemplates";

export type PickerKind =
  | "LoanPicker"
  | "PortfolioPicker"
  | "BrokerPicker"
  | "VendorPicker"
  | "DealPicker";

export interface PickerRecord {
  id: string;
  primary: string;
  secondary?: string | null;
}

interface PickerDrawerProps {
  open: boolean;
  kind: PickerKind;
  selectedId?: string | null;
  onClose: () => void;
  onPick: (record: PickerRecord) => void;
}

const KIND_LABELS: Record<PickerKind, { title: string; description: string }> = {
  LoanPicker: {
    title: "Pick a loan",
    description: "Search the loan portfolio. The picked record auto-fills loan_number, borrower_name, and loan_amount placeholders.",
  },
  PortfolioPicker: {
    title: "Pick a portfolio",
    description: "Auto-fills the portfolio_name placeholder.",
  },
  BrokerPicker: {
    title: "Pick a broker",
    description: "Auto-fills the broker_name placeholder.",
  },
  VendorPicker: {
    title: "Pick a vendor",
    description: "Auto-fills vendor placeholders for the report.",
  },
  DealPicker: {
    title: "Pick a deal",
    description: "Auto-fills the deal_id placeholder.",
  },
};

export function isPickerType(t: QuestionType): t is PickerKind {
  return (
    t === "LoanPicker" ||
    t === "PortfolioPicker" ||
    t === "BrokerPicker" ||
    t === "VendorPicker" ||
    t === "DealPicker"
  );
}

/**
 * LLM &amp; Reports v3.1 §4b — record picker for picker-typed questions.
 * Slides in from the right when the user clicks a picker question on the
 * report form. Selecting a record returns its id (stored as the question's
 * response value) and primary label (rendered in the form for confirmation).
 *
 * Loan / Portfolio / Vendor / Deal pickers fall back to a manual-id input
 * because the corresponding modules haven't shipped yet — every other
 * module is keyed by GUID, so this still produces a valid response. The
 * Broker picker has a real backend behind it.
 */
export function PickerDrawer({ open, kind, selectedId, onClose, onPick }: PickerDrawerProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;
  const meta = KIND_LABELS[kind];

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-onyx/40"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <aside
        className="bg-white-pure w-full max-w-md h-full shadow-lg border-l-[2px] border-l-gold flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-space-3 p-space-4 border-b border-border-light">
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-bold text-h3 text-onyx">{meta.title}</h2>
            <p className="text-small text-gray-mid mt-space-1">{meta.description}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-space-1 rounded-full hover:bg-white-warm"
            aria-label="Close picker"
          >
            <X size={18} aria-hidden />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-space-4">
          {kind === "BrokerPicker" ? (
            <BrokerPickerBody selectedId={selectedId ?? null} onPick={onPick} />
          ) : (
            <ManualIdPickerBody
              kind={kind}
              selectedId={selectedId ?? null}
              onPick={onPick}
            />
          )}
        </div>
      </aside>
    </div>
  );
}

function BrokerPickerBody({
  selectedId,
  onPick,
}: {
  selectedId: string | null;
  onPick: (r: PickerRecord) => void;
}) {
  const [query, setQuery] = useState("");
  const list = useQuery({
    queryKey: ["pickerDrawer", "brokers", query],
    queryFn: () =>
      brokersApi.list({
        page: 1,
        pageSize: 25,
        q: query || undefined,
      }),
  });

  return (
    <>
      <div className="flex items-center gap-space-2 mb-space-3">
        <Search size={16} aria-hidden className="text-gray-mid" />
        <Input
          type="search"
          placeholder="Search brokers…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {list.isLoading ? (
        <p className="text-caption text-gray-mid">Loading brokers…</p>
      ) : list.data?.data.length === 0 ? (
        <p className="text-caption text-gray-mid italic">No brokers match.</p>
      ) : (
        <ul className="flex flex-col gap-space-1">
          {list.data?.data.map((b) => {
            const { primary, secondary } = brokerPickerLabel(b);
            return (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() =>
                    onPick({
                      id: b.id,
                      primary,
                      secondary: secondary || null,
                    })
                  }
                  className={`w-full text-left p-space-2 rounded-md border ${
                    selectedId === b.id
                      ? "border-gold bg-gold/10"
                      : "border-border-light hover:bg-white-warm"
                  }`}
                >
                  <div className="font-semibold text-onyx truncate">{primary}</div>
                  {secondary && (
                    <div className="text-caption text-gray-mid truncate">
                      {secondary}
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function ManualIdPickerBody({
  kind,
  selectedId,
  onPick,
}: {
  kind: PickerKind;
  selectedId: string | null;
  onPick: (r: PickerRecord) => void;
}) {
  const [value, setValue] = useState(selectedId ?? "");
  const [label, setLabel] = useState("");

  const valid = useMemo(() => /^[0-9a-f-]{36}$/i.test(value.trim()), [value]);
  const labelHint = useMemo(() => {
    switch (kind) {
      case "LoanPicker":
        return "Borrower / loan label";
      case "PortfolioPicker":
        return "Portfolio name";
      case "VendorPicker":
        return "Vendor name";
      case "DealPicker":
        return "Deal name / sponsor";
      default:
        return "Display label";
    }
  }, [kind]);

  return (
    <div className="space-y-space-3">
      <p className="text-caption text-gray-mid">
        The {kind.replace("Picker", "").toLowerCase()} module is not yet wired up to a
        searchable list. Paste the record's GUID below — picker auto-resolution
        on the server will populate the corresponding placeholders during
        prompt assembly.
      </p>
      <Input
        placeholder="00000000-0000-0000-0000-000000000000"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        invalid={value.length > 0 && !valid}
      />
      <Input
        placeholder={labelHint}
        value={label}
        onChange={(e) => setLabel(e.target.value)}
      />
      <div className="flex justify-end">
        <Button
          type="button"
          disabled={!valid}
          onClick={() =>
            onPick({
              id: value.trim(),
              primary: label.trim() || value.trim(),
              secondary: null,
            })
          }
        >
          Use this record
        </Button>
      </div>
    </div>
  );
}
